/*
 * Marks the chapter you are currently reading in the guide's sidebar nav (desktop) and
 * pill strip (mobile).
 *
 * The links work without this file -- they are ordinary in-page anchors, and the first
 * chapter is marked active in the markup -- so this only ever upgrades the experience.
 *
 * A scroll handler rather than an IntersectionObserver: chapters here are tall and very
 * unequal (one runs 690px, another 450), so "which chapter crosses a line near the top
 * of the viewport" is both the answer we want and the one that is trivial to compute.
 * An observer would need a rootMargin tuned to the tallest chapter to avoid gaps, and
 * would still misreport when two chapters are on screen at once.
 *
 * THE LINE IS THE CHAPTER'S OWN scroll-margin, not a number picked here. Clicking a link
 * parks that chapter exactly scroll-margin below the top of the viewport -- that is what
 * the property is for, clearing the sticky header. Measuring against anything smaller
 * means the chapter you just jumped to does not count as reached, and the mark stays on
 * the one above it.
 */
(() => {
  class TopicGuide {
    constructor(root) {
      this.root = root;
      this.chapters = Array.from(root.querySelectorAll('[data-mj-chapter]'));
      this.links = Array.from(root.querySelectorAll('[data-mj-chapter-link]'));
      this.strip = root.querySelector('[data-mj-chapter-strip]');
      this.current = null;
      this.ticking = false;
      this.scroller = null;
      this.lines = [];

      if (this.chapters.length === 0 || this.links.length === 0) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      /*
       * Capture, on the document, rather than a listener on window. Above 990px this
       * theme makes html and body 100dvh and scrolls .page-wrapper instead, so the
       * window never fires a scroll event on desktop at all. Scroll events do not
       * bubble, but they do pass through the capture phase from the document down --
       * so this one listener catches whichever element is actually doing the scrolling.
       */
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      addEventListener('resize', this.onResize, { passive: true });
      this.measure();
      this.update();
    }

    /*
     * scroll-margin is written in terms of --header-height, which changes with the
     * viewport, so the lines are re-read on resize rather than once at startup.
     */
    measure() {
      this.lines = this.chapters.map((chapter) => {
        const margin = parseFloat(getComputedStyle(chapter).scrollMarginTop);
        /* One pixel of slack: after a jump the top sits exactly on the line. */
        return (Number.isFinite(margin) ? margin : 0) + 1;
      });
    }

    /* Whichever element is scrolling -- .page-wrapper on desktop, the page on mobile. */
    findScroller() {
      let el = this.root.parentElement;
      while (el) {
        const overflow = getComputedStyle(el).overflowY;
        if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight) {
          return el;
        }
        el = el.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    }

    onResize() {
      this.scroller = null;
      this.measure();
      this.update();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    /* The last chapter whose top has passed its line, falling back to the first. */
    activeId() {
      if (!this.scroller) this.scroller = this.findScroller();
      const sc = this.scroller;

      /*
       * At the very bottom there may be no room left to bring the last chapter up to
       * its line, so it could never be reached by the rule below. Guarded on the page
       * actually being scrollable, or a page that fits entirely would start on its
       * last chapter.
       */
      const scrollable = sc.scrollHeight > sc.clientHeight + 2;
      if (scrollable && sc.scrollHeight - sc.scrollTop - sc.clientHeight <= 2) {
        return this.chapters[this.chapters.length - 1].id;
      }

      let id = this.chapters[0].id;
      this.chapters.forEach((chapter, i) => {
        if (chapter.getBoundingClientRect().top - this.lines[i] <= 0) id = chapter.id;
      });
      return id;
    }

    update() {
      const id = this.activeId();
      if (id === this.current) return;
      this.current = id;

      for (const link of this.links) {
        const active = link.getAttribute('href') === `#${id}`;
        if (active) {
          link.setAttribute('aria-current', 'true');
          this.reveal(link);
        } else {
          link.removeAttribute('aria-current');
        }
      }
    }

    /*
     * Brings the active pill into view inside the strip. scrollIntoView would do this
     * too, but it is also allowed to scroll the PAGE to reach the element -- which,
     * called from a scroll handler, fights the reader for control of the page.
     * Setting scrollLeft can only ever move the strip.
     */
    reveal(link) {
      const strip = this.strip;
      if (!strip || !strip.contains(link) || strip.scrollWidth <= strip.clientWidth) return;

      const pad = parseFloat(getComputedStyle(strip).paddingInlineStart) || 0;
      const left = link.offsetLeft - pad;
      const right = link.offsetLeft + link.offsetWidth + pad;
      let target = strip.scrollLeft;

      if (left < strip.scrollLeft) target = left;
      else if (right > strip.scrollLeft + strip.clientWidth) target = right - strip.clientWidth;
      if (target === strip.scrollLeft) return;

      const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
      strip.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
    }
  }

  const init = (scope) => {
    for (const root of (scope || document).querySelectorAll('[data-mj-topic-guide]')) {
      if (root.dataset.mjTopicGuideReady) continue;
      root.dataset.mjTopicGuideReady = 'true';
      new TopicGuide(root);
    }
  };

  init();
  /* The theme editor swaps section markup in place, so new copies need wiring up. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
